(() => {
  const COMPONENT_NAME = "product-carousel";

  if (customElements.get(COMPONENT_NAME)) return;

  class ProductCarousel extends HTMLElement {
    #abortController = null;
    #panels = [];
    #tabs = [];
    #shopLinks = [];
    #mobileCtas = [];
    #swipers = new Map();
    #activePanelId = null;
    #activeSwiper = null;
    #isSetup = false;
    #setupFrameId = null;
    #swiperWaitAttempts = 0;
    #dynamicPanels = null;
    #bootFailsafeId = null;
    #hydrateTimeoutId = null;

    static #MAX_SWIPER_WAIT_ATTEMPTS = 300;
    /** Cap dynamic panel hydration so hung fetches cannot leave --booting on. */
    static #HYDRATE_TIMEOUT_MS = 10000;
    /** Last-resort reveal if setup never reaches #finishBoot. */
    static #BOOT_FAILSAFE_MS = 12000;

    connectedCallback() {
      this.#abortController = new AbortController();
      this.#swiperWaitAttempts = 0;
      this.#relocateChrome();

      for (const panel of this.querySelectorAll(
        "[data-product-carousel-panel]",
      )) {
        panel.classList.remove("is-active");
        panel.setAttribute("aria-hidden", "true");
        panel.inert = true;
      }

      this.classList.add("product-carousel--enhanced");
      this.#armBootFailsafe();
      this.#initTabs();
      this.#setupWhenReady();
    }

    disconnectedCallback() {
      if (this.#setupFrameId !== null) {
        cancelAnimationFrame(this.#setupFrameId);
        this.#setupFrameId = null;
      }

      this.#clearBootTimers();

      for (const swiper of this.#swipers.values()) {
        swiper.destroy(true, true);
      }

      this.#swipers.clear();
      this.#activeSwiper = null;
      this.#activePanelId = null;
      this.#isSetup = false;
      this.#swiperWaitAttempts = 0;
      this.classList.remove(
        "product-carousel--enhanced",
        "product-carousel--swiper-ready",
      );

      for (const tab of this.#tabs) {
        delete tab.dataset.productCarouselBound;
      }

      this.#panels = [];
      this.#tabs = [];
      this.#shopLinks = [];
      this.#mobileCtas = [];
      this.#dynamicPanels?.abort();
      this.#dynamicPanels = null;
      this.#abortController?.abort();
      this.#abortController = null;
    }

    #setupWhenReady() {
      if (!this.isConnected || this.#isSetup) return;

      if (typeof window.Swiper === "undefined") {
        this.#swiperWaitAttempts += 1;

        if (
          this.#swiperWaitAttempts > ProductCarousel.#MAX_SWIPER_WAIT_ATTEMPTS
        ) {
          this.#isSetup = true;
          this.#setupNativeControls();
          return;
        }

        this.#setupFrameId = requestAnimationFrame(() => {
          this.#setupFrameId = null;
          this.#setupWhenReady();
        });
        return;
      }

      if (!this.#abortController) return;

      this.#isSetup = true;
      this.classList.add("product-carousel--swiper-ready");
      this.#setup();
      void this.#completeBoot();
    }

    #setup() {
      const controller = this.#abortController;
      if (!controller || !this.isConnected) return;

      this.#relocateChrome();
      this.#bindNavControls();
    }

    #setupNativeControls() {
      const controller = this.#abortController;
      if (!controller || !this.isConnected) return;

      this.#relocateChrome();
      this.#bindNavControls();
      void this.#completeBoot();
    }

    async #completeBoot() {
      let timedOut = false;

      try {
        const timeoutPromise = new Promise((resolve) => {
          this.#hydrateTimeoutId = setTimeout(() => {
            timedOut = true;
            resolve();
          }, ProductCarousel.#HYDRATE_TIMEOUT_MS);
        });

        await Promise.race([this.#hydrateDynamicPanels(), timeoutPromise]);
      } catch {
        // Hydration errors must not leave the carousel stuck in --booting.
      } finally {
        if (this.#hydrateTimeoutId !== null) {
          clearTimeout(this.#hydrateTimeoutId);
          this.#hydrateTimeoutId = null;
        }
      }

      if (!this.isConnected) return;

      if (timedOut) {
        this.#dynamicPanels?.abort();
      }

      this.#revealSsrFallbackPanels();
      this.#initTabs();
      this.#initSwiperForActivePanel();
      this.#finishBoot();
    }

    #armBootFailsafe() {
      this.#clearBootFailsafe();
      this.#bootFailsafeId = setTimeout(() => {
        this.#bootFailsafeId = null;

        if (!this.isConnected) return;
        if (!this.classList.contains("product-carousel--booting")) return;

        this.#dynamicPanels?.abort();
        this.#revealSsrFallbackPanels();
        this.#initTabs();
        this.#initSwiperForActivePanel();
        this.#finishBoot();
      }, ProductCarousel.#BOOT_FAILSAFE_MS);
    }

    #clearBootFailsafe() {
      if (this.#bootFailsafeId !== null) {
        clearTimeout(this.#bootFailsafeId);
        this.#bootFailsafeId = null;
      }
    }

    #clearBootTimers() {
      this.#clearBootFailsafe();

      if (this.#hydrateTimeoutId !== null) {
        clearTimeout(this.#hydrateTimeoutId);
        this.#hydrateTimeoutId = null;
      }
    }

    #revealSsrFallbackPanels() {
      for (const panel of this.querySelectorAll(
        ".product-carousel-panel--pending",
      )) {
        if (
          !panel.querySelector(".swiper-slide.product-carousel-panel__slide")
        ) {
          continue;
        }

        panel.hidden = false;
        panel.classList.remove("product-carousel-panel--pending");

        const panelId = panel.dataset.productCarouselPanel;
        if (!panelId) continue;

        for (const chrome of this.querySelectorAll(
          `[data-product-carousel-tab="${panelId}"], [data-product-carousel-shop-link="${panelId}"], [data-product-carousel-mobile-cta="${panelId}"]`,
        )) {
          chrome.hidden = false;
        }
      }
    }

    #finishBoot() {
      this.#clearBootFailsafe();
      this.classList.remove("product-carousel--booting");
      this.removeAttribute("aria-busy");
    }

    #bindNavControls() {
      const controller = this.#abortController;
      if (!controller) return;

      const { signal } = controller;
      const prevButton = this.querySelector("[data-product-carousel-prev]");
      const nextButton = this.querySelector("[data-product-carousel-next]");

      prevButton?.addEventListener(
        "click",
        () => {
          if (this.#activeSwiper) {
            this.#activeSwiper.slidePrev();
            return;
          }

          this.#scrollActiveTrack(-1);
        },
        { signal },
      );
      nextButton?.addEventListener(
        "click",
        () => {
          if (this.#activeSwiper) {
            this.#activeSwiper.slideNext();
            return;
          }

          this.#scrollActiveTrack(1);
        },
        { signal },
      );
    }

    #scrollActiveTrack(direction) {
      const panel = this.#getPanelById(this.#activePanelId);
      const track = panel?.querySelector(".swiper-wrapper");
      if (!track) return;

      const amount = Math.max(track.clientWidth * 0.8, 1) * direction;
      track.scrollBy({ left: amount, behavior: "smooth" });
    }

    #initTabs() {
      this.#panels = [
        ...this.querySelectorAll("[data-product-carousel-panel]"),
      ];
      this.#tabs = [
        ...this.querySelectorAll(
          "[data-product-carousel-tabs] [data-product-carousel-tab]",
        ),
      ];
      this.#shopLinks = [
        ...this.querySelectorAll(
          "[data-product-carousel-shop-links] [data-product-carousel-shop-link]",
        ),
      ];
      this.#mobileCtas = [
        ...this.querySelectorAll(
          "[data-product-carousel-mobile-ctas] [data-product-carousel-mobile-cta]",
        ),
      ];

      this.#upgradeTabSemantics();

      const isPanelSelectable = (panel) =>
        Boolean(panel) &&
        !panel.hidden &&
        !panel.classList.contains("product-carousel-panel--empty") &&
        !panel.classList.contains("product-carousel-panel--pending");

      const visiblePanels = this.#panels.filter(isPanelSelectable);

      const activeStillValid =
        this.#activePanelId != null &&
        visiblePanels.some(
          (panel) => panel.dataset.productCarouselPanel === this.#activePanelId,
        );

      const tabForVisiblePanel = this.#tabs.find((tab) =>
        isPanelSelectable(this.#getPanelById(tab.dataset.productCarouselTab)),
      );

      const defaultPanelId =
        visiblePanels[0]?.dataset.productCarouselPanel ??
        tabForVisiblePanel?.dataset.productCarouselTab ??
        null;

      if (!activeStillValid && defaultPanelId) {
        this.#activePanelId = null;
        this.#activatePanel(defaultPanelId, false);
      } else if (defaultPanelId && !this.#activePanelId) {
        this.#activatePanel(defaultPanelId, false);
      } else if (this.#activePanelId && activeStillValid) {
        this.#syncActiveChrome(this.#activePanelId);
      } else if (this.#activePanelId && !activeStillValid) {
        this.#activePanelId = null;
        this.#syncActiveChrome("");
      }

      const controller = this.#abortController;
      if (!controller) return;

      const { signal } = controller;

      for (const tab of this.#tabs) {
        if (tab.dataset.productCarouselBound === "true") continue;

        tab.dataset.productCarouselBound = "true";
        tab.addEventListener(
          "click",
          () => this.#activatePanel(tab.dataset.productCarouselTab),
          { signal },
        );
      }
    }

    #upgradeTabSemantics() {
      const tablist = this.querySelector("[data-product-carousel-tabs]");

      if (tablist) {
        tablist.setAttribute("role", "tablist");

        if (tablist.dataset.tabsLabel) {
          tablist.setAttribute("aria-label", tablist.dataset.tabsLabel);
        }
      }

      const upgradedTabs = [];

      for (const tab of this.#tabs) {
        const panelId = tab.dataset.productCarouselTab;
        let tabButton = tab;
        const isNewButton = tab.tagName !== "BUTTON";

        if (isNewButton) {
          tabButton = document.createElement("button");
          tabButton.type = "button";
          tabButton.className = tab.className;
          tabButton.id = tab.id;
          tabButton.dataset.productCarouselTab = panelId;
          tabButton.textContent = tab.textContent.trim();
          tabButton.hidden = tab.hidden;

          if (tab.dataset.productCarouselBound === "true") {
            tabButton.dataset.productCarouselBound = "true";
          }

          tab.replaceWith(tabButton);
          tabButton.setAttribute("aria-selected", "false");
          tabButton.tabIndex = -1;
        }

        tabButton.setAttribute("role", "tab");
        tabButton.setAttribute(
          "aria-controls",
          `ProductCarouselPanel-${panelId}`,
        );
        upgradedTabs.push(tabButton);
      }

      this.#tabs = upgradedTabs;

      for (const panel of this.#panels) {
        const panelId = panel.dataset.productCarouselPanel;
        panel.setAttribute("role", "tabpanel");
        panel.setAttribute("aria-labelledby", `ProductCarouselTab-${panelId}`);
      }
    }

    #syncActiveChrome(panelId) {
      for (const panelElement of this.#panels) {
        const isActive = panelElement.dataset.productCarouselPanel === panelId;
        panelElement.classList.toggle("is-active", isActive);
        panelElement.setAttribute("aria-hidden", isActive ? "false" : "true");
        panelElement.inert = !isActive;
      }

      for (const tab of this.#tabs) {
        const isActive = tab.dataset.productCarouselTab === panelId;
        tab.setAttribute("aria-selected", isActive ? "true" : "false");
        tab.tabIndex = isActive ? 0 : -1;
      }

      for (const link of this.#shopLinks) {
        link.hidden = link.dataset.productCarouselShopLink !== panelId;
      }

      for (const cta of this.#mobileCtas) {
        cta.hidden = cta.dataset.productCarouselMobileCta !== panelId;
      }
    }

    #relocateChrome() {
      const tabsContainer = this.querySelector("[data-product-carousel-tabs]");
      const shopContainer = this.querySelector(
        "[data-product-carousel-shop-links]",
      );
      const mobileContainer = this.querySelector(
        "[data-product-carousel-mobile-ctas]",
      );

      for (const tab of this.querySelectorAll(
        ".product-carousel__panels [data-product-carousel-tab]",
      )) {
        tabsContainer?.appendChild(tab);
      }

      for (const link of this.querySelectorAll(
        ".product-carousel__panels [data-product-carousel-shop-link]",
      )) {
        shopContainer?.appendChild(link);
      }

      for (const cta of this.querySelectorAll(
        ".product-carousel__panels [data-product-carousel-mobile-cta]",
      )) {
        mobileContainer?.appendChild(cta);
      }
    }

    #getCarouselGap() {
      const gapValue = getComputedStyle(this)
        .getPropertyValue("--product-carousel-gap")
        .trim();
      const parsed = Number.parseInt(gapValue, 10);

      return Number.isNaN(parsed) ? 16 : parsed;
    }

    #getPanelById(panelId) {
      return this.#panels.find(
        (panel) => panel.dataset.productCarouselPanel === panelId,
      );
    }

    #getOrInitSwiper(panel) {
      if (typeof window.Swiper === "undefined") return null;

      const panelId = panel.dataset.productCarouselPanel;
      const existingSwiper = this.#swipers.get(panelId);

      if (existingSwiper && !existingSwiper.destroyed) {
        return existingSwiper;
      }

      const swiperElement = panel.querySelector(
        "[data-product-carousel-swiper]",
      );
      if (!swiperElement) return null;

      const wasHidden = !panel.classList.contains("is-active");
      if (wasHidden) {
        panel.classList.add("is-active");
        panel.setAttribute("aria-hidden", "false");
        panel.inert = false;
      }

      const swiper = new window.Swiper(swiperElement, {
        slidesPerView: "auto",
        slidesPerGroup: 1,
        spaceBetween: this.#getCarouselGap(),
        allowTouchMove: true,
        simulateTouch: true,
        grabCursor: true,
        mousewheel: {
          forceToAxis: true,
          releaseOnEdges: true,
        },
        watchOverflow: false,
        observer: true,
        observeParents: true,
        resistanceRatio: 0.85,
        roundLengths: true,
      });

      if (wasHidden) {
        panel.classList.remove("is-active");
        panel.setAttribute("aria-hidden", "true");
        panel.inert = true;
      }

      swiper.on("slideChange", () => {
        if (panelId === this.#activePanelId) {
          this.#updateProgress(swiper);
        }
      });

      this.#swipers.set(panelId, swiper);
      return swiper;
    }

    #activatePanel(panelId, focusTab = true) {
      if (!panelId) return;

      const isSamePanel = panelId === this.#activePanelId;
      const panel = this.#getPanelById(panelId);
      if (!panel) return;

      if (!isSamePanel) {
        this.#activePanelId = panelId;
        this.#syncActiveChrome(panelId);

        if (focusTab) {
          const activeTab = this.#tabs.find(
            (tab) => tab.dataset.productCarouselTab === panelId,
          );
          activeTab?.focus();
        }
      }

      this.#initSwiperForActivePanel();
    }

    #initSwiperForActivePanel() {
      if (typeof window.Swiper === "undefined" || !this.#activePanelId) return;

      const panel = this.#getPanelById(this.#activePanelId);
      if (!panel) return;

      this.#activeSwiper = this.#getOrInitSwiper(panel);

      requestAnimationFrame(() => {
        if (!this.isConnected) return;

        this.#activeSwiper?.update();
        this.#updateProgress(this.#activeSwiper);
      });
    }

    #updateProgress(swiper) {
      const progress = this.querySelector("[data-product-carousel-progress]");
      if (!progress) return;

      const snapCount = swiper ? Math.max(1, swiper.snapGrid?.length ?? 1) : 1;
      const activeIndex = snapCount > 1 ? (swiper?.snapIndex ?? 0) : 0;

      // Replacing the segments on every slide change is why the bar used to
      // jump: a freshly created element has no previous width to animate
      // from, so the browser paints it at its final size. Only rebuild when
      // the count actually changes, then move the active class.
      if (progress.children.length !== snapCount) {
        progress.replaceChildren(
          ...Array.from({ length: snapCount }, () =>
            this.#createProgressSegment(),
          ),
        );
      }

      for (const [index, segment] of [...progress.children].entries()) {
        segment.classList.toggle(
          "carousel-pagination__bullet--active",
          index === activeIndex,
        );
      }
    }

    #createProgressSegment() {
      const segment = document.createElement("span");
      segment.className = "carousel-pagination__bullet";
      return segment;
    }

    async #hydrateDynamicPanels() {
      if (typeof window.ProductCarouselDynamicPanels !== "function") return;

      const hasDynamicPanels = this.querySelector(
        "[data-product-carousel-source]",
      );

      if (!hasDynamicPanels) return;

      this.#dynamicPanels?.abort();
      this.#dynamicPanels = new window.ProductCarouselDynamicPanels(this);

      // Defer tab/swiper activation until every dynamic panel has settled so
      // SSR fallback → recommendations replace never paints mid-hydrate.
      await this.#dynamicPanels.hydrate({});
    }
  }

  customElements.define(COMPONENT_NAME, ProductCarousel);
})();
